import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Building2,
  Landmark,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  Send,
  Calendar,
  FileText,
  Hash,
  PoundSterling
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreditorRequestFormPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    request_type: location.state?.type || 'credit_request',
    store_id: location.state?.assignment?.store_id || '',
    creditor_id: location.state?.assignment?.creditor_id || '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    reference: '',
    description: ''
  });

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/creditor-requests/my-assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignments(response.data);
      
      // If no assignment pre-selected and we have assignments, select first one
      if (!formData.store_id && response.data.length > 0) {
        setFormData(prev => ({
          ...prev,
          store_id: response.data[0].store_id,
          creditor_id: response.data[0].creditor_id
        }));
      }
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      toast.error('Failed to load creditor accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentChange = (value) => {
    const [storeId, creditorId] = value.split('-');
    setFormData(prev => ({
      ...prev,
      store_id: storeId,
      creditor_id: creditorId
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.store_id || !formData.creditor_id) {
      toast.error('Please select a creditor account');
      return;
    }
    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount greater than £0');
      return;
    }

    try {
      setSubmitting(true);
      
      await axios.post(`${API}/api/creditor-requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(
        formData.request_type === 'credit_request' 
          ? 'Credit request submitted successfully' 
          : 'Repayment logged successfully'
      );
      
      navigate('/creditor-dashboard');
    } catch (error) {
      console.error('Failed to submit request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedAssignment = assignments.find(
    a => a.store_id === formData.store_id && a.creditor_id === formData.creditor_id
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="creditor-request-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {formData.request_type === 'credit_request' ? 'Submit Credit Request' : 'Log Repayment'}
          </h1>
          <p className="text-muted-foreground">
            {formData.request_type === 'credit_request' 
              ? 'Request credit owed to your account' 
              : 'Log a payment received from this account'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {formData.request_type === 'credit_request' ? (
                <TrendingUp className="h-5 w-5 text-red-400" />
              ) : (
                <TrendingDown className="h-5 w-5 text-green-400" />
              )}
              Request Details
            </CardTitle>
            <CardDescription>
              Fill in the details for your {formData.request_type === 'credit_request' ? 'credit request' : 'repayment'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Request Type Toggle */}
            <div className="space-y-2">
              <Label>Request Type</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={formData.request_type === 'credit_request' ? 'default' : 'outline'}
                  className={`h-auto py-3 ${formData.request_type === 'credit_request' ? 'border-red-500/50' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, request_type: 'credit_request' }))}
                  data-testid="credit-type-btn"
                >
                  <div className="flex flex-col items-center gap-1">
                    <TrendingUp className={`h-5 w-5 ${formData.request_type === 'credit_request' ? 'text-red-400' : ''}`} />
                    <span>Credit Request</span>
                    <span className="text-xs text-muted-foreground">Money owed to you</span>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={formData.request_type === 'repayment_request' ? 'default' : 'outline'}
                  className={`h-auto py-3 ${formData.request_type === 'repayment_request' ? 'border-green-500/50' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, request_type: 'repayment_request' }))}
                  data-testid="repayment-type-btn"
                >
                  <div className="flex flex-col items-center gap-1">
                    <TrendingDown className={`h-5 w-5 ${formData.request_type === 'repayment_request' ? 'text-green-400' : ''}`} />
                    <span>Repayment</span>
                    <span className="text-xs text-muted-foreground">Payment received</span>
                  </div>
                </Button>
              </div>
            </div>

            {/* Creditor Account */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Landmark className="h-4 w-4" />
                Creditor Account
              </Label>
              <Select
                value={formData.store_id ? `${formData.store_id}-${formData.creditor_id}` : ''}
                onValueChange={handleAssignmentChange}
              >
                <SelectTrigger data-testid="account-select">
                  <SelectValue placeholder="Select creditor account" />
                </SelectTrigger>
                <SelectContent>
                  {assignments.map(a => (
                    <SelectItem key={`${a.store_id}-${a.creditor_id}`} value={`${a.store_id}-${a.creditor_id}`}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{a.store_name}</span>
                        <span className="text-muted-foreground">-</span>
                        <span>{a.creditor_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAssignment && (
                <p className="text-xs text-muted-foreground">
                  Store: {selectedAssignment.store_name} • Creditor: {selectedAssignment.creditor_name}
                </p>
              )}
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Date
              </Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                data-testid="date-input"
              />
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <PoundSterling className="h-4 w-4" />
                Amount
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  className="pl-8"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  data-testid="amount-input"
                />
              </div>
            </div>

            {/* Reference */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Hash className="h-4 w-4" />
                Reference (Optional)
              </Label>
              <Input
                type="text"
                placeholder="e.g., Invoice #12345, Payment ref..."
                value={formData.reference}
                onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                data-testid="reference-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Description (Optional)
              </Label>
              <Textarea
                placeholder="Add any additional notes or details..."
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                data-testid="description-input"
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1"
                disabled={submitting}
                data-testid="submit-request-btn"
              >
                {submitting ? (
                  <>
                    <div className="spinner mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit {formData.request_type === 'credit_request' ? 'Credit Request' : 'Repayment'}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
